import { Marker } from "react-leaflet";
import { useTranslation } from "react-i18next";
import MapPopUpTags from "./MapPopUpTags";
import { memo } from "react";
import companyMarkerIcon from "./companyMarkerIcon";

// Inline SVG icons (no @mui/icons-material). MUI components generate emotion
// styles at runtime; mounting that subtree on every popup open caused a
// noticeable click lag even on fast hardware. Plain HTML/CSS opens instantly.
const Svg = ({ path, className }) => (
  <svg className={className} viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d={path} />
  </svg>
);
const ICONS = {
  language: "M12 2a10 10 0 100 20 10 10 0 000-20zm6.93 6h-2.95a15.7 15.7 0 00-1.38-3.56A8.03 8.03 0 0118.93 8zM12 4c.83 1.2 1.48 2.53 1.91 4h-3.82c.43-1.47 1.08-2.8 1.91-4zM4.26 14a7.8 7.8 0 010-4h3.38a16.6 16.6 0 000 4H4.26zm.81 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 015.07 16zm2.95-8H5.07a7.99 7.99 0 014.33-3.56A15.7 15.7 0 008.02 8zM12 20c-.83-1.2-1.48-2.53-1.91-4h3.82A13.6 13.6 0 0112 20zm2.34-6H9.66a14.8 14.8 0 010-4h4.68a14.8 14.8 0 010 4zm.27 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 01-4.33 3.56zM16.36 14a16.6 16.6 0 000-4h3.38a7.8 7.8 0 010 4h-3.38z",
  email: "M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  place: "M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z",
  person: "M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z",
  facebook: "M22 12a10 10 0 10-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0022 12z",
  twitter: "M22 5.8c-.7.32-1.5.54-2.3.64a4 4 0 001.76-2.2c-.78.46-1.64.8-2.56.98a4 4 0 00-6.8 3.64A11.34 11.34 0 013.6 4.7a4 4 0 001.24 5.33c-.65-.02-1.26-.2-1.8-.5v.05a4 4 0 003.2 3.92c-.6.16-1.23.18-1.84.07a4 4 0 003.73 2.78A8 8 0 012 18.3a11.32 11.32 0 006.13 1.8c7.35 0 11.37-6.09 11.37-11.37v-.52A8.1 8.1 0 0022 5.8z",
  linkedin: "M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14zM8.34 18.34V9.99H5.67v8.35h2.67zM7 8.8a1.55 1.55 0 100-3.1 1.55 1.55 0 000 3.1zm11.34 9.54v-4.58c0-2.45-1.31-3.59-3.06-3.59-1.41 0-2.04.78-2.39 1.32v-1.13h-2.67c.04.75 0 8.35 0 8.35h2.67v-4.66c0-.24.02-.48.09-.65.19-.48.62-.97 1.35-.97.96 0 1.34.72 1.34 1.78v4.5h2.67z",
};

// Lightweight marker only. The heavy MUI popup body is rendered once, by Map,
// in a single shared <Popup> for the selected company — mounting a full popup
// subtree for every marker (the previous approach) froze the main thread on
// first load and made react-leaflet close/reopen the popup (visible blink).
function CompanyMarker({ industrieObject, onSelect }) {
  return (
    <Marker
      position={[industrieObject.Coordinates.Latitude, industrieObject.Coordinates.Longitude]}
      icon={companyMarkerIcon}
      eventHandlers={{ click: () => onSelect(industrieObject) }}
    />
  );
}

export default memo(CompanyMarker);

export function PopupContent({ industrieObject }) {
  const { t } = useTranslation();

  const rawCareerUrl = industrieObject.Website_career || industrieObject.Website;
  const careerUrl = rawCareerUrl
    ? (rawCareerUrl.startsWith('http') ? rawCareerUrl : `https://${rawCareerUrl}`)
    : null;

  const url = industrieObject.Website
    ? (industrieObject.Website.startsWith('http') ? industrieObject.Website : `https://${industrieObject.Website}`)
    : null;

  const share = (urlBase) => () => window.open(urlBase + encodeURIComponent(careerUrl), '_blank', 'noopener');

  return (
    <div className="popup-card">
      <div className="popup-header">
        <h3 className="popup-title">
          {industrieObject.Company}
          {industrieObject.Industrie_object_name ? ` (${industrieObject.Industrie_object_name})` : ''}
        </h3>
        {(industrieObject.company_type || industrieObject.company_size) && (
          <div className="popup-chips">
            {industrieObject.company_type && (
              <span className="popup-chip">{industrieObject.company_type}</span>
            )}
            {industrieObject.company_size && (
              <span className="popup-chip">{t("company_size." + industrieObject.company_size)}</span>
            )}
          </div>
        )}
      </div>

      {Array.isArray(industrieObject.product_services) && industrieObject.product_services.length > 0 && (
        <MapPopUpTags text={t('popup.products_and_services')} tags={industrieObject.product_services} />
      )}
      {Array.isArray(industrieObject.field_profiles) && industrieObject.field_profiles.length > 0 && (
        <MapPopUpTags text={t('popup.field_profiles')} tags={industrieObject.field_profiles} />
      )}
      {Array.isArray(industrieObject.technologies) && industrieObject.technologies.length > 0 && (
        <MapPopUpTags text={t('popup.technologies')} tags={industrieObject.technologies} />
      )}
      {Array.isArray(industrieObject.type_of_industries) && industrieObject.type_of_industries.length > 0 && (
        <MapPopUpTags text={t('popup.type_of_industry')} tags={industrieObject.type_of_industries} />
      )}

      <hr className="popup-divider" />

      <div className="popup-contact">
        {industrieObject.Website && (
          <div className="popup-contact-row">
            <Svg className="popup-contact-icon" path={ICONS.language} />
            <span className="popup-contact-text">
              <a className="popup-link" href={url} target="_blank" rel="noopener noreferrer">
                {industrieObject.Website}
              </a>
            </span>
          </div>
        )}
        {industrieObject.Email && (
          <div className="popup-contact-row">
            <Svg className="popup-contact-icon" path={ICONS.email} />
            <span className="popup-contact-text">{industrieObject.Email}</span>
          </div>
        )}
        {industrieObject.Street_and_haus_number && industrieObject.ZIP_Code && industrieObject.Location && (
          <div className="popup-contact-row">
            <Svg className="popup-contact-icon" path={ICONS.place} />
            <span className="popup-contact-text">
              {industrieObject.Street_and_haus_number}, {industrieObject.ZIP_Code} {industrieObject.Location}
            </span>
          </div>
        )}
        {industrieObject.Contact_information && (
          <div className="popup-contact-row">
            <Svg className="popup-contact-icon" path={ICONS.person} />
            <span className="popup-contact-text">{industrieObject.Contact_information}</span>
          </div>
        )}
      </div>

      {careerUrl && (
        <a className="apply-button" href={careerUrl} target="_blank" rel="noopener noreferrer">
          {t('popup.apply')}
        </a>
      )}

      {careerUrl && (
        <div className="popup-social">
          <button type="button" className="popup-social-btn" aria-label="Facebook"
                  onClick={share('https://www.facebook.com/sharer/sharer.php?u=')}>
            <Svg path={ICONS.facebook} />
          </button>
          <button type="button" className="popup-social-btn" aria-label="Twitter"
                  onClick={share('https://twitter.com/intent/tweet?url=')}>
            <Svg path={ICONS.twitter} />
          </button>
          <button type="button" className="popup-social-btn" aria-label="LinkedIn"
                  onClick={share('https://www.linkedin.com/shareArticle?mini=true&url=')}>
            <Svg path={ICONS.linkedin} />
          </button>
        </div>
      )}
    </div>
  );
}
